import { ArrowUpRight, Building2, Shield, Sparkles } from "lucide-react";

// Six-module suite. WorkSafeStory (this product) is highlighted.
const SUITE = [
  { name: "WSafe AI", tag: "AI hazard detection on existing cameras", href: "https://wsafe.ai" },
  { name: "HazardLoop", tag: "Corrective action tracking and verified closeout", href: "https://hazardloop.com" },
  { name: "WorkSafeStory", tag: "Safety stories and lessons learned", active: true },
  { name: "InductIQ", tag: "Induction/training verification and incident reporting", href: "https://inductiq.ai" },
  { name: "EnviSafe", tag: "Environmental conditions monitoring", href: "https://envisafe.ai" },
  { name: "SafeForm", tag: "Ergonomic analysis", href: "https://safeform.ai" },
];

/**
 * Marketing shell — surfaces WorkSafeStory's position inside the WorkSafeAgent
 * platform. Does not alter any product-app functionality. Matches the site's
 * Swiss high-contrast style (yellow/orange/black on white, hard shadows, no
 * rounded corners).
 */
export default function PartOfWorkSafeAgent() {
  return (
    <section
      className="border-y-2 border-[#09090B] bg-[#09090B] text-white"
      data-testid="part-of-worksafeagent-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {/* Header row */}
        <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-start">
          <div className="w-14 h-14 bg-[#FFCC00] text-[#09090B] flex items-center justify-center">
            <Building2 strokeWidth={2.5} className="w-7 h-7" />
          </div>
          <div>
            <div className="ws-label text-[#FFCC00]">/ Part of WorkSafeAgent</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2 leading-tight">
              PREVENT THE INCIDENT.<br />
              <span className="text-[#FFCC00]">DON&apos;T RECONSTRUCT IT.</span>
            </h2>
            <p className="text-sm sm:text-base text-[#D4D4D8] mt-4 max-w-2xl leading-relaxed">
              WorkSafeStory is one of six modules in a single operational safety platform, sold as
              a <strong className="text-white">Founding paid pilot</strong> and Core / Pro / Vision
              site licenses. This module handles{" "}
              <strong className="text-white">safety stories and lessons learned</strong>{" "}
              — included in <strong className="text-white">Core, Pro, and Vision</strong>.
            </p>
          </div>
          <div className="border-2 border-[#FFCC00] bg-[#FF5A00] text-white px-5 py-4 ws-hard-shadow-sm min-w-[180px]">
            <div className="ws-label text-white/90">Founding paid pilot</div>
            <div className="font-display font-black text-3xl tracking-tighter mt-1">$1,000</div>
            <div className="ws-label text-white/90 mt-1">No install fee</div>
          </div>
        </div>

        {/* Suite grid */}
        <div className="mt-12">
          <div className="ws-label text-[#FFCC00] flex items-center gap-2">
            <Sparkles strokeWidth={2.5} className="w-4 h-4" /> Suite · Six modules · One platform
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {SUITE.map((m) => {
              const Tag = m.href ? "a" : "div";
              const linkProps = m.href ? { href: m.href, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Tag
                  key={m.name}
                  {...linkProps}
                  data-testid={`suite-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`border-2 p-4 block ${
                    m.active
                      ? "border-[#FFCC00] bg-[#FFCC00] text-[#09090B] ws-hard-shadow-sm"
                      : "border-[#FFCC00]/40 bg-transparent text-white hover:border-[#FFCC00] transition-colors"
                  }`}
                >
                  <div className="font-display font-bold text-base tracking-tight">{m.name}</div>
                  <div className={`text-xs leading-snug mt-1 ${m.active ? "text-[#09090B]" : "text-[#FFCC00]"}`}>
                    {m.tag}
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>

        {/* Links + credibility */}
        <div className="mt-12 grid md:grid-cols-2 gap-6 items-start">
          <div className="border-2 border-[#FFCC00]/40 p-5">
            <div className="ws-label text-[#FFCC00]">Company hub</div>
            <a
              href="https://worksafeagent.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="link-worksafeagent"
              className="mt-2 inline-flex items-center gap-2 font-display font-bold text-lg tracking-tight text-white hover:text-[#FFCC00] transition-colors"
            >
              worksafeagent.com <ArrowUpRight strokeWidth={2.5} className="w-4 h-4" />
            </a>
            <div className="mt-4 ws-label text-[#FFCC00]">Promo · WSafe AI</div>
            <a
              href="https://wsafe.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="link-wsafe-ai"
              className="mt-2 inline-flex items-center gap-2 font-display font-bold text-lg tracking-tight text-white hover:text-[#FFCC00] transition-colors"
            >
              wsafe.ai <ArrowUpRight strokeWidth={2.5} className="w-4 h-4" />
            </a>
          </div>

          <div className="border-2 border-[#FFCC00]/40 p-5" data-testid="credibility-block">
            <div className="ws-label text-[#FFCC00] flex items-center gap-2">
              <Shield strokeWidth={2.5} className="w-4 h-4" /> Credibility
            </div>
            <ul className="mt-3 text-sm text-[#D4D4D8] space-y-2 leading-relaxed">
              <li>
                <span className="text-white font-bold">Patent-pending</span> — USPTO provisional
                {" "}<span className="font-mono">63/992,261</span> (filed Feb 27, 2026)
              </li>
              <li>
                Method published — Zenodo DOI{" "}
                <a
                  href="https://doi.org/10.5281/zenodo.20499515"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-zenodo"
                  className="font-mono text-white hover:text-[#FFCC00] underline decoration-[#FFCC00]/60 underline-offset-4"
                >
                  10.5281/zenodo.20499515
                </a>
              </li>
              <li>
                ORCID{" "}
                <a
                  href="https://orcid.org/0009-0000-2089-0803"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-orcid"
                  className="font-mono text-white hover:text-[#FFCC00] underline decoration-[#FFCC00]/60 underline-offset-4"
                >
                  0009-0000-2089-0803
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
